import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield, Globe, Server, Activity, AlertTriangle, Clock,
  TrendingUp, Database, Wifi, ChevronRight, RefreshCw,
  BarChart3, PieChart, Map, List, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

// API endpoint for dashboard data
const DASHBOARD_API_URL = 'https://api.threat.live/cache/v2_dashboard_cache.json';

const StatsPage = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const translations = {
    tr: {
      title: 'Tehdit İstatistikleri',
      subtitle: 'Gerçek zamanlı siber tehdit analizi ve görselleştirme',
      overview: 'Genel Bakış',
      threats: 'Tehditler',
      geography: 'Coğrafya',
      network: 'Ağ',
      timeline: 'Zaman Çizelgesi',
      // Summary cards
      totalRecords: 'Toplam Kayıt',
      uniqueIPs: 'Benzersiz IP',
      confirmedIPs: 'Onaylı IP',
      newToday: 'Son 24 Saat',
      abuseReported: 'Abuse Bildirilen',
      // Sections
      threatTypes: 'Tehdit Türleri',
      topCountries: 'En Çok Tehdit Üreten Ülkeler',
      topTargetCountries: 'En Çok Tehdit Alan Ülkeler',
      topASNs: 'En Aktif ASN\'ler',
      portDistribution: 'Port Dağılımı',
      nodeStats: 'Node İstatistikleri',
      dailyTrend: 'Günlük Trend',
      recentIPs: 'Son Eklenen IP\'ler',
      last24hCountries: 'Son 24 Saat - Ülkeler',
      last24hASNs: 'Son 24 Saat - ASN\'ler',
      protocolDist: 'Protokol Dağılımı',
      // Table headers
      ip: 'IP Adresi',
      service: 'Servis',
      org: 'Organizasyon',
      country: 'Ülke',
      time: 'Zaman',
      node: 'Node',
      records: 'Kayıt',
      ips: 'IP Sayısı',
      // Misc
      lastUpdate: 'Son Güncelleme',
      refreshing: 'Yenileniyor...',
      records: 'Kayıt',
      ipCount: 'IP Sayısı',
      threatMap: 'Tehdit Haritası',
      threatMapDesc: 'Dünya genelinde tehdit kaynaklarının dağılımı',
      high: 'Yüksek',
      low: 'Düşük',
    },
    en: {
      title: 'Threat Statistics',
      subtitle: 'Real-time cyber threat analysis and visualization',
      overview: 'Overview',
      threats: 'Threats',
      geography: 'Geography',
      network: 'Network',
      timeline: 'Timeline',
      totalRecords: 'Total Records',
      uniqueIPs: 'Unique IPs',
      confirmedIPs: 'Confirmed IPs',
      newToday: 'Last 24 Hours',
      abuseReported: 'Abuse Reported',
      threatTypes: 'Threat Types',
      topCountries: 'Top Threat Sources by Country',
      topTargetCountries: 'Top Targeted Countries',
      topASNs: 'Most Active ASNs',
      portDistribution: 'Port Distribution',
      nodeStats: 'Node Statistics',
      dailyTrend: 'Daily Trend',
      recentIPs: 'Recently Added IPs',
      last24hCountries: 'Last 24 Hours - Countries',
      last24hASNs: 'Last 24 Hours - ASNs',
      protocolDist: 'Protocol Distribution',
      ip: 'IP Address',
      service: 'Service',
      org: 'Organization',
      country: 'Country',
      time: 'Time',
      node: 'Node',
      records: 'Records',
      ips: 'IP Count',
      lastUpdate: 'Last Update',
      refreshing: 'Refreshing...',
      records: 'Records',
      ipCount: 'IP Count',
      threatMap: 'Threat Map',
      threatMapDesc: 'Global distribution of threat sources',
      high: 'High',
      low: 'Low',
    }
  };

  const txt = translations[language] || translations.en;

  // Color palette for charts
  const COLORS = ['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];
  const THREAT_COLORS = {
    'Bruteforce': '#ef4444',
    'DDoS': '#f59e0b', 
    'Web Tarama': '#8b5cf6',
    'Ağ Tarama': '#06b6d4',
    'Diğer': '#6b7280'
  };

  // Custom tooltip style
  const tooltipStyle = {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
  };

  const tooltipLabelStyle = {
    color: '#f3f4f6',
    fontWeight: '500',
    marginBottom: '4px',
  };

  const tooltipItemStyle = {
    color: '#d1d5db',
    fontSize: '13px',
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label, valueFormatter, labelFormatter }) => {
    if (active && payload && payload.length) {
      return (
        <div style={tooltipStyle}>
          {label && (
            <p style={tooltipLabelStyle}>
              {labelFormatter ? labelFormatter(label) : label}
            </p>
          )}
          {payload.map((entry, index) => (
            <p key={index} style={{ ...tooltipItemStyle, color: entry.color || '#d1d5db' }}>
              <span style={{ color: entry.color, marginRight: '6px' }}>●</span>
              {entry.name}: {valueFormatter ? valueFormatter(entry.value) : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(DASHBOARD_API_URL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const jsonData = await response.json();
        setDashboardData(jsonData);
        setData(jsonData.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError(language === 'tr' 
          ? 'Veriler yüklenirken hata oluştu. Lütfen sayfayı yenileyin.'
          : 'Failed to load data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [language]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  // Relative time formatter
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const past = new Date(timestamp.replace(' ', 'T'));
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (language === 'tr') {
      if (diffMins < 1) return 'Az önce';
      if (diffMins < 60) return `${diffMins} dakika önce`;
      if (diffHours < 24) return `${diffHours} saat önce`;
      return `${diffDays} gün önce`;
    } else {
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      return `${diffDays} days ago`;
    }
  };

  const getCountryFlag = (isoCode) => {
    if (!isoCode) return '🌐';
    const codePoints = isoCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  // Skeleton component for loading state
  const Skeleton = ({ className }) => (
    <div className={`animate-pulse bg-muted/50 rounded ${className}`} />
  );

  // Loading state with skeletons
  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="stats-page-loading">
        <Header />
        
        {/* Hero Section Skeleton */}
        <section className="relative py-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <Skeleton className="h-10 w-64 mb-2" />
                <Skeleton className="h-5 w-96" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
          </div>
        </section>

        <main className="container mx-auto px-4 pb-16 -mt-4">
          {/* Summary Cards Skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-24 mb-3" />
                  <Skeleton className="h-8 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2 border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full" />
              </CardContent>
            </Card>
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-28" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full rounded-full mx-auto" style={{ maxWidth: '200px' }} />
              </CardContent>
            </Card>
          </div>

          {/* Bar Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader className="pb-2">
                  <Skeleton className="h-6 w-40" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...Array(8)].map((_, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 flex-1" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              {language === 'tr' ? 'Hata Oluştu' : 'Error Occurred'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Yeniden Dene' : 'Try Again'}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="text-center">
            <Database className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">
              {language === 'tr' ? 'Veri Bulunamadı' : 'No Data Found'}
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { summary, by_threat_type, by_country, by_asn, by_port, by_port_name, by_protocol, timeseries_daily, nodes, by_node, recent_ips, last24h_by_country, last24h_by_asn, source_country_to_node } = data;

  // Prepare chart data
  const threatTypeData = by_threat_type?.map(item => ({
    name: item.threat_type,
    value: item.unique_ip_count,
    records: item.record_count,
    fill: THREAT_COLORS[item.threat_type] || '#6b7280'
  })) || [];

  const countryData = by_country?.slice(0, 15).map(item => ({
    name: item.country,
    iso: item.iso_code,
    ips: item.unique_ip_count,
    records: item.record_count
  })) || [];

  // Calculate target countries (countries receiving threats) from source_country_to_node
  const targetCountryMap = {};
  source_country_to_node?.forEach(item => {
    const country = item.node_country;
    if (!targetCountryMap[country]) {
      targetCountryMap[country] = { name: country, ips: 0, records: 0 };
    }
    targetCountryMap[country].ips += item.unique_ip_count;
    targetCountryMap[country].records += item.record_count;
  });
  const targetCountryData = Object.values(targetCountryMap)
    .sort((a, b) => b.ips - a.ips)
    .slice(0, 15);

  const asnData = by_asn?.slice(0, 10).map(item => ({
    name: item.org?.length > 25 ? item.org.substring(0, 25) + '...' : item.org,
    fullName: item.org,
    asn: item.asn,
    ips: item.unique_ip_count
  })) || [];

  const portData = by_port_name?.slice(0, 12).map(item => ({
    name: item.name === 'Port 99' ? 'DDoS' : item.name,
    value: item.count
  })) || [];

  // Format date to "21 Şubat" or "21 Feb" style
  const formatDateLabel = (dateStr) => {
    const months = language === 'tr' 
      ? ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const [year, month, day] = dateStr.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    return `${parseInt(day, 10)} ${months[monthIndex]}`;
  };

  const timelineData = timeseries_daily?.map(item => ({
    date: formatDateLabel(item.date),
    fullDate: item.date,
    records: item.record_count,
    ips: item.unique_ip_count
  })) || [];

  const protocolData = Object.entries(by_protocol || {}).map(([name, value]) => ({
    name,
    value
  })).filter(item => item.value > 0);

  const nodeData = by_node?.map(item => ({
    name: `${item.node_city}, ${item.node_country}`,
    city: item.node_city,
    country: item.node_country,
    ips: item.unique_ip_count,
    records: item.record_count
  })).sort((a, b) => b.ips - a.ips).slice(0, 10) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="stats-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl -z-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                <span className="bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                  {txt.title}
                </span>
              </h1>
              <p className="text-muted-foreground">{txt.subtitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {txt.lastUpdate}: {getRelativeTime(dashboardData.timestamp)}
              </Badge>
              <Badge variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30">
                v{dashboardData.cache_version}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 pb-16 -mt-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Card className="border-0 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 hover:from-emerald-500/20 hover:to-emerald-500/10 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-emerald-500" />
                <span className="text-xs text-muted-foreground">{txt.totalRecords}</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary?.total_records)}</p>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 hover:from-cyan-500/20 hover:to-cyan-500/10 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="h-4 w-4 text-cyan-500" />
                <span className="text-xs text-muted-foreground">{txt.uniqueIPs}</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary?.total_unique_ips)}</p>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-violet-500/10 to-violet-500/5 hover:from-violet-500/20 hover:to-violet-500/10 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-violet-500" />
                <span className="text-xs text-muted-foreground">{txt.confirmedIPs}</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary?.confirmed_unique_ips)}</p>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-amber-500/10 to-amber-500/5 hover:from-amber-500/20 hover:to-amber-500/10 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">{txt.newToday}</span>
              </div>
              <p className="text-2xl font-bold text-amber-500">+{formatNumber(summary?.new_unique_ips_24h)}</p>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-pink-500/10 to-pink-500/5 hover:from-pink-500/20 hover:to-pink-500/10 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-pink-500" />
                <span className="text-xs text-muted-foreground">{txt.abuseReported}</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary?.abuseipdb_reported_unique_ips)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Daily Trend Chart - Spans 2 columns */}
          <Card className="lg:col-span-2 border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-emerald-500" />
                {txt.dailyTrend}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timelineData}>
                    <defs>
                      <linearGradient id="colorRecords" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorIps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={formatNumber} />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      labelFormatter={(label) => `Tarih: ${label}`}
                    />
                    <Area type="monotone" dataKey="records" stroke="#10b981" fillOpacity={1} fill="url(#colorRecords)" name={txt.records} />
                    <Area type="monotone" dataKey="ips" stroke="#06b6d4" fillOpacity={1} fill="url(#colorIps)" name={txt.ipCount} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Threat Types Pie Chart */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="h-5 w-5 text-emerald-500" />
                {txt.threatTypes}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={threatTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {threatTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      formatter={(value, name, props) => [
                        `${formatNumber(value)} IP`,
                        props.payload.name
                      ]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {threatTypeData.map((item, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                    <span className="text-xs text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row - Countries and ASNs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Top Source Countries (Threat Origins) */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Map className="h-5 w-5 text-red-500" />
                {txt.topCountries}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={countryData} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickFormatter={formatNumber} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#9ca3af" 
                      fontSize={10} 
                      width={90}
                      tickFormatter={(value) => value.length > 10 ? value.substring(0, 10) + '...' : value}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      formatter={(value, name, props) => [`${formatNumber(value)} IP`, props.payload.name]}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="ips" fill="#ef4444" radius={[0, 4, 4, 0]} name={txt.ipCount} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Target Countries (Receiving Threats) */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-500" />
                {txt.topTargetCountries}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={targetCountryData} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickFormatter={formatNumber} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#9ca3af" 
                      fontSize={10} 
                      width={90}
                      tickFormatter={(value) => value.length > 10 ? value.substring(0, 10) + '...' : value}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      formatter={(value, name, props) => [`${formatNumber(value)} IP`, props.payload.name]}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="ips" fill="#10b981" radius={[0, 4, 4, 0]} name={txt.ipCount} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top ASNs */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="h-5 w-5 text-cyan-500" />
                {txt.topASNs}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={asnData} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickFormatter={formatNumber} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#9ca3af" 
                      fontSize={9} 
                      width={110}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      formatter={(value, name, props) => [`${formatNumber(value)} IP`, props.payload.fullName]}
                      labelFormatter={(label) => `AS${asnData.find(a => a.name === label)?.asn || ''}`}
                    />
                    <Bar dataKey="ips" fill="#06b6d4" radius={[0, 4, 4, 0]} name={txt.ipCount} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Third Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Port Distribution */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wifi className="h-5 w-5 text-emerald-500" />
                {txt.portDistribution}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {portData.map((item, index) => {
                  const maxValue = Math.max(...portData.map(p => p.value));
                  const percentage = (item.value / maxValue) * 100;
                  return (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">{item.name}</span>
                        <span className="font-medium">{formatNumber(item.value)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Protocol Distribution */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-500" />
                {txt.protocolDist}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={protocolData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {protocolData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={tooltipItemStyle}
                      formatter={(value, name, props) => [`${formatNumber(value)} ${txt.records}`, props.payload.name]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Node Stats */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="h-5 w-5 text-emerald-500" />
                {txt.nodeStats}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {nodeData.map((node, index) => {
                  const maxValue = Math.max(...nodeData.map(n => n.ips));
                  const percentage = (node.ips / maxValue) * 100;
                  return (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground">{node.name}</span>
                        <span className="font-medium text-emerald-500">{formatNumber(node.ips)}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Last 24h Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Last 24h Countries */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                {txt.last24hCountries}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
                {last24h_by_country?.slice(0, 20).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCountryFlag(item.iso_code)}</span>
                      <span className="text-sm truncate max-w-[100px]">{item.country}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{formatNumber(item.unique_ip_count)}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Last 24h ASNs */}
          <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                {txt.last24hASNs}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                {last24h_by_asn?.slice(0, 10).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.org}</p>
                      <p className="text-xs text-muted-foreground">AS{item.asn}</p>
                    </div>
                    <Badge className="ml-2 bg-amber-500/20 text-amber-500 border-amber-500/30">
                      {formatNumber(item.unique_ip_count)} IP
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent IPs Table */}
        <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <List className="h-5 w-5 text-emerald-500" />
              {txt.recentIPs}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.ip}</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.service}</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.org}</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.country}</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.node}</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">{txt.time}</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_ips?.slice(0, 15).map((ip, index) => (
                    <tr key={index} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2 px-2">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{ip.ip_address}</code>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className="text-xs">
                          {ip.port === 99 || ip.service === 'Port 99' ? 'DDoS' : (ip.service || '-')}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs truncate max-w-[150px] block">{ip.org}</span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="flex items-center gap-1">
                          <span>{getCountryFlag(ip.iso_code)}</span>
                          <span className="text-xs">{ip.country}</span>
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className="text-xs">{ip.node_city}, {ip.node_country}</Badge>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-muted-foreground">{ip.created_at?.substring(11, 19)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default StatsPage;
